import React, { useState, useEffect, useMemo } from 'react';
import { Alert, Skeleton } from 'antd';
import CamposSimulador from '../../molecules/CamposSimulador';
import DrawerEmpresa from '../../molecules/DrawerEmpresa';
import TabelaRendimentoLiquido from '../../molecules/TabelaRendimentoLiquido';
import TabelaDescontos from '../../molecules/TabelaDescontos';
import RodapeSimulador from '../../molecules/RodapeSimulador';
import ModalEmailColaborador from '../../molecules/ModalEmailColaborador';
import { findSettings } from '../../../../../services/repository/settingRepository';
import CalculadoraRE from '../../../../../services/CalculadoraRE';
import gerarArquivo from './gerarArquivo';
import { useGetAliquotas } from '../../../../../services/repository/aliquotaImpostoRepository';
import useFields from './useFields';
import useCompanyFields from './useCompanyFields';

function SimuladorRE() {
  const { data: aliquotasImposto, error, loading } = useGetAliquotas();
  const settings = findSettings();

  const [isDrawerEmpresaVisivel, setIsDrawerEmpresaVisivel] = useState(false);
  const [isModalEmailVisible, setIsModalEmailVisible] = useState(false);
  const [isAdministrativo, setIsAdministrativo] = useState(false);

  const {
    fields,
    setFields,
    updateFieldValue,
    hideField,
    revealField,
    enableField,
    disableField,
  } = useFields(settings);
  const [companyFields, setCompanyFields, updateCompanyFieldValue] =
    useCompanyFields(settings);

  const [remuneracaoTotal, setRemuneracaoTotal] = useState(0);
  const [rendimentoLiquido, setRendimentoLiquido] = useState({});
  const [descontos, setDescontos] = useState({});
  const [custosEmpresa, setCustosEmpresa] = useState({});

  const calculadoraRE = useMemo(() => {
    const allFields = { ...fields, ...companyFields };
    return new CalculadoraRE(
      allFields,
      aliquotasImposto && aliquotasImposto.simulador
    );
  }, [fields, companyFields, aliquotasImposto]);

  const toggleAdministrativo = () => {
    const newIsAdministrativoValue = !isAdministrativo;
    setIsAdministrativo(newIsAdministrativoValue);
    if (newIsAdministrativoValue) {
      disableField('ajudaCusto');
      hideField('pida');
    } else {
      enableField('ajudaCusto');
      revealField('pida');
    }
  };

  const updateInpuFields = () =>
    setFields((previousFields) => ({
      ...previousFields,
      salario: { ...previousFields.salario, value: calculadoraRE.getSalario() },
      transporte: {
        ...previousFields.transporte,
        value: calculadoraRE.getTransporte(),
      },
      beneficios: {
        ...previousFields.beneficios,
        value: calculadoraRE.getBeneficios(),
      },
      ajudaCusto: {
        ...previousFields.ajudaCusto,
        value: calculadoraRE.getAjudaCusto(isAdministrativo),
      },
      pida: {
        ...previousFields.pida,
        value: calculadoraRE.getPida(isAdministrativo),
      },
    }));

  const updateCompanyInputFields = () =>
    setCompanyFields((previousCompanyFields) => {
      const {
        assistenciaMedica,
        odontologico,
        outrosCustos,
        alimentacaoRefeicao,
        percentualGps,
      } = previousCompanyFields;

      return {
        ...previousCompanyFields,
        assistenciaMedica: {
          ...assistenciaMedica,
          value: calculadoraRE.getAssistenciaMedica(),
        },
        odontologico: {
          ...odontologico,
          value: calculadoraRE.getOdontologico(),
        },
        outrosCustos: {
          ...outrosCustos,
          value: calculadoraRE.getOutrosCustos(),
        },
        alimentacaoRefeicao: {
          ...alimentacaoRefeicao,
          value: calculadoraRE.getAlimentacaoRefeicao(),
        },
        percentualGps: {
          ...percentualGps,
          value: calculadoraRE.getPercentualGps(),
        },
      };
    });

  const calculate = (applySettings = true) => {
    if (applySettings) {
      calculadoraRE.applySettings();
    }
    updateInpuFields();
    updateCompanyInputFields();

    setRemuneracaoTotal(calculadoraRE.getRemuneracaoTotal());
    setRendimentoLiquido({
      pat: calculadoraRE.getPat(),
      salarioLiquido: calculadoraRE.getSalarioLiquido(),
      liquidoComplementar: calculadoraRE.getLiquidoComplementar(),
      totalLiquido: calculadoraRE.getTotalLiquido(),
    });

    setDescontos({
      inss: calculadoraRE.getInss(),
      irrf: calculadoraRE.getIrrf(),
      irrfComplementar: calculadoraRE.getIrrfComplementar(),
    });

    setCustosEmpresa({
      fgts: calculadoraRE.getFgts(),
      gpsInss: calculadoraRE.getGpsInss(),
      provisaoDecimoTerceiro: calculadoraRE.getProvisaoDecimoTerceiro(),
      encargosDecimoTerceiro: calculadoraRE.getEncargosDecimoTerceiro(),
      feriasUmTerco: calculadoraRE.getFeriasUmTerco(),
      encargosFeriasUmTerco: calculadoraRE.getEncargosFeriasUmTerco(),
      fgtsRescisao: calculadoraRE.getFgtsRescisao(),
      totalEncargos: calculadoraRE.getTotalEncargos(),
      totalBeneficios: calculadoraRE.getTotalBeneficios(),
      custoTotal: calculadoraRE.getCustoTotal(),
    });
  };

  useEffect(() => {
    if (!loading) {
      calculate();
    }
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    calculate();
  }, [isAdministrativo]); // eslint-disable-line react-hooks/exhaustive-deps

  const onBlurField = (id) => {
    calculate(id === 'remuneracaoBase');
  }; // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <Skeleton paragraph={{ rows: 5 }} active />;
  }

  return (
    <>
      {error && (
        <Alert
          style={{
            wordBreak: 'break-word',
            whiteSpace: 'break-spaces',
            marginBottom: '16px',
          }}
          message="Devido a um erro interno, não foi possível buscar as alíquotas de impostos utilizadas nos cálculos. Lamentamos o ocorrido, nossa equipe já foi informada"
          showIcon
          type="error"
        />
      )}

      <CamposSimulador
        fields={fields}
        onChangeFieldValue={updateFieldValue}
        onBlurField={onBlurField}
        remuneracaoTotal={remuneracaoTotal}
        toggleAdministrativo={toggleAdministrativo}
        isAdministrativo={isAdministrativo}
      />

      <TabelaRendimentoLiquido rendimentoLiquido={rendimentoLiquido} />
      <TabelaDescontos descontos={descontos} />

      <RodapeSimulador
        onEmpresaClick={() => setIsDrawerEmpresaVisivel(true)}
        onExportarArquivoClick={() =>
          gerarArquivo(calculadoraRE, isAdministrativo)
        }
        onEnviarEmailClick={() => setIsModalEmailVisible(true)}
      />

      {/* Componentes Extras */}
      <DrawerEmpresa
        fields={companyFields}
        isVisible={isDrawerEmpresaVisivel}
        onClose={() => setIsDrawerEmpresaVisivel(false)}
        onChangeFieldValue={updateCompanyFieldValue}
        onBlurField={onBlurField}
        custosEmpresa={custosEmpresa}
      />

      <ModalEmailColaborador
        isVisible={isModalEmailVisible}
        onCancelClick={() => setIsModalEmailVisible(false)}
        onOkClick={() => setIsModalEmailVisible(false)}
        calculadoraRE={calculadoraRE}
      />
    </>
  );
}

export default SimuladorRE;
